import React, {FC, CSSProperties} from "react";
import {Checkbox, Link, Typography} from "@material-ui/core";
import ErrorIcon from "@material-ui/icons/Error";
import ClauseChangeComment from "./clause_change_comment";
import StatusBadge from "./status_badge";
import {
  PreviewParagraph,
  PreviewSegment,
  ProcessedClauseChange,
  UnitPreview,
} from "common/flowmaster/types/amendment";
import {stripClauseNumberPrefix} from "word/taskpane/routes/checklist/utils/amendments/clause_number_utils";
import {getClauseChangeScrollTarget} from "utils/amendments/get_amendment_anchor_text";
import formatClauseLabel from "utils/amendments/format_clause_label";
import type {ScrollToAmendmentClause} from "./amendment_group";
import {Uuid} from "common/types";

type AmendmentType = "deletion" | "addition" | "modification";

interface ChangeTypeInfo {
  type: AmendmentType;
  style: CSSProperties;
}

function clipBeforeText(text: string): string {
  const lastPunctuationIndex = Math.max(
    text.lastIndexOf("."),
    text.lastIndexOf("!"),
    text.lastIndexOf("?"),
  );
  let result = text;
  if (lastPunctuationIndex !== -1 && lastPunctuationIndex < text.length - 1) {
    result = text.substring(lastPunctuationIndex + 1).trimStart();
  }
  if (result && !result.endsWith(" ")) {
    result += " ";
  }
  return result;
}

function clipAfterText(text: string): string {
  let result = text;
  const punctuationMatch = text.match(/[.!?]/);
  if (punctuationMatch && punctuationMatch.index !== undefined) {
    result = text.substring(0, punctuationMatch.index + 1);
  }
  if (result && !result.startsWith(" ")) {
    result = ` ${result}`;
  }
  return result;
}

const styles: Record<string, CSSProperties> = {
  container: {
    margin: "6px 0px",
    padding: "16px",
  },
  deletion: {
    backgroundColor: "#ffebee",
    border: "1px solid #ffcdd2",
  },
  addition: {
    backgroundColor: "#e8f5e9",
    border: "1px solid #c8e6c9",
  },
  modification: {
    backgroundColor: "#e3f2fd",
    border: "1px solid #bbdefb",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },
  content: {
    marginTop: "12px",
  },
  label: {
    color: "#757575",
    fontSize: "0.9rem",
    marginBottom: "4px",
  },
  deletedText: {
    color: "#d32f2f",
    textDecoration: "line-through",
    marginTop: "4px",
  },
  addedText: {
    color: "#2e7d32",
    marginTop: "4px",
    display: "inline",
  },
  contextText: {
    color: "#000000",
    marginTop: "4px",
    display: "inline",
  },
  modifiedText: {
    fontSize: "1rem",
    lineHeight: 1.5,
    fontFamily: "inherit",
  },
  textSection: {
    marginTop: "8px",
  },
  errorMessage: {
    marginTop: "12px",
    color: "#d32f2f",
    fontSize: "0.9rem",
    fontWeight: "bold",
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
  },
  errorIcon: {
    fontSize: "16px",
    color: "#d32f2f",
  },
  commentsContainer: {
    marginTop: "8px",
    padding: "8px",
  },
};

const segmentStyle = (kind: PreviewSegment["kind"]): CSSProperties => {
  if (kind === "deleted") {
    return {color: "#d32f2f", textDecoration: "line-through"};
  }
  if (kind === "added") {
    return {color: "#2e7d32"};
  }
  return {color: "#000000"};
};

const paragraphStyle = (paragraph: PreviewParagraph): CSSProperties => ({
  marginLeft: `${paragraph.indent * 16}px`,
  marginTop: "4px",
  fontSize: "1rem",
  lineHeight: 1.5,
});

const UnitPreviewView: FC<{preview: UnitPreview}> = ({preview}) => (
  <div>
    {preview.paragraphs.map(paragraph =>
      paragraph.status === "collapsed" ? (
        <div
          key={paragraph.key}
          style={{
            ...paragraphStyle(paragraph),
            color: "#616161",
            fontSize: "0.8rem",
            fontStyle: "italic",
          }}
        >
          {paragraph.collapsedCount === 1
            ? "1 unchanged paragraph not shown"
            : `${paragraph.collapsedCount} unchanged paragraphs not shown`}
        </div>
      ) : (
        <div
          key={paragraph.key}
          style={{
            ...paragraphStyle(paragraph),
            ...(paragraph.isHeading && {fontWeight: 600}),
          }}
        >
          {paragraph.label && (
            <span
              style={{
                fontWeight: 500,
                marginRight: "6px",
                ...(paragraph.status === "deleted" && segmentStyle("deleted")),
                ...(paragraph.status === "inserted" && segmentStyle("added")),
              }}
            >
              {paragraph.label}
            </span>
          )}
          {paragraph.segments.map((segment, i) => (
            <span key={i} style={segmentStyle(segment.kind)}>
              {segment.text}
            </span>
          ))}
        </div>
      ),
    )}
  </div>
);

interface ClauseChangeProps {
  clauseChange: ProcessedClauseChange;
  isCommentSelected: boolean;
  isSelected: boolean;
  isCompleted: boolean;
  showUnappliedBadge?: boolean;
  onSelectChange: (checked: boolean) => void;
  onCommentSelectChange: (checked: boolean) => void;
  onIndividualCommentSelectChange?: (commentId: Uuid, checked: boolean) => void;
  selectedRelatedComments?: Uuid[];
  errorMessage?: string;
  scrollToClause?: ScrollToAmendmentClause;
  siblingAddedTexts?: string[];
}

const ClauseChangeItem: FC<ClauseChangeProps> = ({
  clauseChange,
  isCommentSelected,
  isSelected,
  isCompleted,
  showUnappliedBadge,
  onSelectChange,
  onCommentSelectChange,
  onIndividualCommentSelectChange,
  selectedRelatedComments = [],
  errorMessage,
  scrollToClause,
  siblingAddedTexts = [],
}) => {
  const getChangeType = (): ChangeTypeInfo => {
    if (clauseChange.delete) {
      return {type: "deletion", style: styles.deletion};
    } else if (clauseChange.insert_after || clauseChange.insert_before) {
      return {type: "addition", style: styles.addition};
    }
    return {type: "modification", style: styles.modification};
  };

  const changeType = getChangeType();
  // Only an unnumbered paragraph needs context to say where it goes
  const insertSubclauses = clauseChange.rawAmendment?.subclauses ?? [];
  const showInsertContext =
    insertSubclauses.length > 0 &&
    insertSubclauses.every(sub => sub.unnumbered === "true");
  const title = formatClauseLabel(
    clauseChange.clause_reference ?? clauseChange.clause_id,
    clauseChange.section_reference,
  );
  const scrollTarget = getClauseChangeScrollTarget(clauseChange);
  const canScroll = Boolean(scrollToClause && scrollTarget);

  // Chained edits anchor on text a sibling amendment inserts, which is not in
  // the document yet. Don't show such an anchor as if it were existing context.
  const isSiblingInsertedText = (
    text: string,
    ownAddedText: string,
  ): boolean => {
    const needle = text.trim();
    return (
      needle.length > 0 &&
      siblingAddedTexts.some(
        added => added !== ownAddedText && added.trim() === needle,
      )
    );
  };

  const renderContext = (
    text: string | undefined,
    ownAddedText: string,
    isOnNewline: boolean | undefined,
    clip: (value: string) => string,
  ) => {
    if (!text || isSiblingInsertedText(text, ownAddedText)) {
      return null;
    }
    return (
      <span
        style={{
          ...styles.contextText,
          display: isOnNewline ? "block" : "inline",
        }}
      >
        {clip(text)}
      </span>
    );
  };

  const renderInlineDiff = (change: ProcessedClauseChange) => (
    <>
      {renderContext(
        change.replacement_context?.before,
        change.added_text,
        change.replacement_context?.is_before_on_newline,
        clipBeforeText,
      )}
      {change.deleted_text && (
        <span
          style={{...styles.deletedText, display: "inline", marginRight: "4px"}}
        >
          {change.deleted_text}
        </span>
      )}
      <span style={styles.addedText}>{change.added_text}</span>
      {renderContext(
        change.replacement_context?.after,
        change.added_text,
        change.replacement_context?.is_after_on_newline,
        clipAfterText,
      )}
    </>
  );

  return (
    <div
      style={{
        ...styles.container,
        ...changeType.style,
        opacity: isCompleted ? 0.5 : 1,
      }}
    >
      <div style={styles.header}>
        <Checkbox
          checked={isSelected}
          onChange={e => onSelectChange(e.target.checked)}
          style={{padding: "0px"}}
          disabled={isCompleted}
        />
        {canScroll ? (
          <Link
            component="button"
            type="button"
            variant="subtitle1"
            color="primary"
            onClick={() => {
              if (scrollTarget) {
                scrollToClause?.(scrollTarget);
              }
            }}
            style={{textAlign: "left", fontWeight: 500}}
          >
            {title}
          </Link>
        ) : (
          <Typography variant="subtitle1">{title}</Typography>
        )}
        {isCompleted && <StatusBadge applied />}
        {showUnappliedBadge && <StatusBadge applied={false} />}
      </div>
      <div style={styles.content}>
        {clauseChange.unit_preview && (
          <div style={styles.textSection}>
            <Typography style={styles.label}>Redline preview:</Typography>
            <UnitPreviewView preview={clauseChange.unit_preview} />
            {clauseChange.unit_preview.unplaced.length > 0 && (
              <div style={{marginTop: "12px"}}>
                <Typography style={styles.label}>
                  Could not place in preview:
                </Typography>
                {clauseChange.unit_preview.unplaced.map(change => (
                  <div key={change.id} style={styles.modifiedText}>
                    {renderInlineDiff(change)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {!clauseChange.unit_preview && changeType.type === "deletion" && (
          <div style={styles.textSection}>
            <Typography style={styles.label}>Delete</Typography>
            {clauseChange.deleted_text && (
              <div style={styles.modifiedText}>
                {renderContext(
                  clauseChange.replacement_context?.before,
                  clauseChange.added_text,
                  clauseChange.replacement_context?.is_before_on_newline,
                  clipBeforeText,
                )}
                <span style={{...styles.deletedText, display: "inline"}}>
                  {clauseChange.deleted_text}
                </span>
                {renderContext(
                  clauseChange.replacement_context?.after,
                  clauseChange.added_text,
                  clauseChange.replacement_context?.is_after_on_newline,
                  clipAfterText,
                )}
              </div>
            )}
          </div>
        )}
        {!clauseChange.unit_preview && changeType.type === "modification" && (
          <div style={styles.textSection}>
            <Typography style={styles.label}>Modified text:</Typography>
            {clauseChange.grouped &&
            clauseChange.modified_text &&
            clauseChange.modified_text !== clauseChange.original_text ? (
              <div
                style={styles.modifiedText}
                dangerouslySetInnerHTML={{__html: clauseChange.modified_text}}
              />
            ) : clauseChange.grouped && clauseChange.related_changes ? (
              clauseChange.related_changes.map(rc => (
                <div key={rc.id} style={styles.modifiedText}>
                  {renderInlineDiff(rc)}
                </div>
              ))
            ) : (
              <div style={styles.modifiedText}>
                {renderInlineDiff(clauseChange)}
              </div>
            )}
          </div>
        )}
        {!clauseChange.unit_preview && changeType.type === "addition" && (
          <div style={styles.textSection}>
            <Typography style={styles.label}>
              {clauseChange.insert_before ? "Insert before" : "Insert after"}{" "}
              clause:
            </Typography>
            {showInsertContext &&
              renderContext(
                clauseChange.replacement_context?.before,
                clauseChange.added_text,
                true,
                clipBeforeText,
              )}
            {clauseChange.rawAmendment?.subclauses?.length ? (
              <div>
                {clauseChange.rawAmendment.subclauses.map((sub, i, arr) => {
                  const prevRef =
                    i > 0 ? arr[i - 1].target_clause_reference : undefined;
                  const showRef =
                    sub.unnumbered !== "true" &&
                    sub.target_clause_reference &&
                    sub.target_clause_reference !== prevRef;
                  return (
                    <Typography
                      key={i}
                      style={{
                        ...styles.addedText,
                        display: "block",
                        marginLeft: `${(Number(sub.level) || 0) * 16}px`,
                      }}
                    >
                      {showRef && (
                        <span style={{fontWeight: 500}}>
                          {sub.target_clause_reference}{" "}
                        </span>
                      )}
                      {showRef
                        ? stripClauseNumberPrefix(sub.added_text)
                        : sub.added_text}
                    </Typography>
                  );
                })}
              </div>
            ) : (
              <Typography style={styles.addedText}>
                {clauseChange.added_text}
              </Typography>
            )}
            {showInsertContext &&
              renderContext(
                clauseChange.replacement_context?.after,
                clauseChange.added_text,
                true,
                clipAfterText,
              )}
          </div>
        )}
      </div>
      {errorMessage && (
        <div style={styles.errorMessage}>
          <ErrorIcon style={styles.errorIcon} />
          {errorMessage}
        </div>
      )}
      {clauseChange.grouped && clauseChange.related_changes ? (
        <div style={styles.commentsContainer}>
          {clauseChange.related_changes.map(rel =>
            rel.comment_text ? (
              <ClauseChangeComment
                key={rel.id}
                text={rel.comment_text}
                type={changeType.type}
                isSelected={selectedRelatedComments.includes(rel.id)}
                isDisabled={isCompleted}
                isClauseSelected={isSelected}
                onToggle={() =>
                  onIndividualCommentSelectChange &&
                  onIndividualCommentSelectChange(
                    rel.id,
                    !selectedRelatedComments.includes(rel.id),
                  )
                }
              />
            ) : null,
          )}
        </div>
      ) : (
        clauseChange.comment_text && (
          <ClauseChangeComment
            text={clauseChange.comment_text}
            type={changeType.type}
            isSelected={isCommentSelected}
            isDisabled={isCompleted}
            isClauseSelected={isSelected}
            onToggle={() => onCommentSelectChange(!isCommentSelected)}
          />
        )
      )}
    </div>
  );
};

export default ClauseChangeItem;
